import * as React from 'react';

import type { CampaignDetailDto } from '@/api/campaign-client';
import { useExportsClient } from '@/api/exports-client-context';
import { useAddParticipation, useRemoveParticipation } from '@/api/use-campaigns';
import { useSuppliersList } from '@/api/use-suppliers';
import { Button } from '@/components/ui/button';
import { Panel } from '@/components/ui/panel';
import { participationStateLabel } from '@/lib/campaign-labels';
import { formatSiret } from '@/routes/admin/campaigns/campaign-suppliers-focus';
import { plural, TitleMono } from '@/routes/admin/campaigns/detail-ui';
import {
  EntityScopePicker,
  type ScopeCandidate,
  type ScopeMember,
} from '@/routes/admin/campaigns/entity-scope-picker';

// =============================================================================
// suppliers-panel.tsx — Panneau « Fournisseurs » du détail campagne (participations).
// =============================================================================
// Extrait de campaign-detail-screen.tsx (ParticipationSection) : recherche SERVER-SIDE par dénomination,
// ajout / retrait d'une participation, badge d'état (tous états), export CSV des destinataires
// (§2.7.3 / A-16, lecture seule). Habillage : Panel à lignes plates (maquette 2026-09-16).
//
// LOT F1-bis (GATE_HUMAN 2026-09-21) — la zone d'ajout devient une liste déroulante AUTOFILTRANTE
// multi-sélection. Le référentiel fournisseurs n'est PAS chargé côté client (il est paginé serveur), ce
// qui impose deux régimes, tous deux demandés :
//   AU REPOS, la liste montre la PREMIÈRE PAGE renvoyée par le serveur (`useSuppliersList({})`), que
//   `FilterCombo` retrie alphabétiquement. C'est la « liste visuelle des premiers fournisseurs ».
//   À LA FRAPPE, la recherche serveur (`q`, ≥ 2 caractères, DEBOUNCÉE à 300 ms en parité avec le
//   registre fournisseurs) alimente la liste ; le filtre local du composant resserre immédiatement
//   l'affichage pendant l'aller-retour réseau, sans attendre la réponse.
// Le seuil de 2 caractères est une contrainte de l'API, pas un choix d'interface : sous 2 caractères
// on interroge sans `q` et la première page sert de matière.

export function SuppliersPanel({
  campaign,
}: {
  readonly campaign: CampaignDetailDto;
}): React.JSX.Element {
  const add = useAddParticipation();
  const remove = useRemoveParticipation();
  const exportsClient = useExportsClient();
  // Texte frappé (immédiat) et texte ÉMIS vers le serveur (différé). Le composant filtre déjà en local
  // sur `query` ; le debounce ne retarde donc que l'appel réseau, jamais le retour visuel.
  const [query, setQuery] = React.useState('');
  const [emitted, setEmitted] = React.useState('');
  React.useEffect(() => {
    const timer = setTimeout(() => setEmitted(query.trim()), 300);
    return () => {
      clearTimeout(timer);
    };
  }, [query]);

  const searchTrim = emitted;
  const addedSirets = React.useMemo(
    () => campaign.participations.map((p) => p.supplierSiret),
    [campaign.participations],
  );
  const addedSiretsSet = React.useMemo(() => new Set(addedSirets), [addedSirets]);

  // Recherche SERVER-SIDE par dénomination (`q`) — ≥ 2 caractères. Batch séparé (filtre `sirets`)
  // pour résoudre les dénominations des déjà-participants (leur nom n'est pas porté par la participation).
  const searchQuery = useSuppliersList(searchTrim.length >= 2 ? { q: searchTrim } : {});
  const membersQuery = useSuppliersList(
    addedSirets.length > 0 ? { sirets: addedSirets } : {},
  );

  const nameBySiret = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const it of membersQuery.data?.items ?? []) {
      m.set(it.siret, it.raisonSocialeLegale ?? it.siret);
    }
    for (const it of searchQuery.data?.items ?? []) {
      if (!m.has(it.siret)) {
        m.set(it.siret, it.raisonSocialeLegale ?? it.siret);
      }
    }
    return m;
  }, [membersQuery.data, searchQuery.data]);

  // Candidats = la page courante du serveur moins les participants déjà invités. Aucun plafond local :
  // la borne est celle de la pagination serveur, et elle est DITE sous le déclencheur (cf. `searchNote`).
  const candidates = React.useMemo<readonly ScopeCandidate[]>(
    () =>
      (searchQuery.data?.items ?? [])
        .filter((it) => !addedSiretsSet.has(it.siret))
        .map((it) => ({ id: it.siret, denomination: it.raisonSocialeLegale ?? it.siret })),
    [searchQuery.data, addedSiretsSet],
  );

  // Honnêteté de la liste : quand le serveur a plus de fournisseurs que la page rendue, on le DIT
  // plutôt que de laisser croire que la liste est exhaustive.
  const page = searchQuery.data;
  const truncated = page !== undefined && page.total > page.items.length;

  const members = React.useMemo<readonly ScopeMember[]>(
    () =>
      campaign.participations.map((p) => {
        const s = participationStateLabel(p.state);
        return {
          entryId: p.participationId,
          id: p.supplierSiret,
          denomination: nameBySiret.get(p.supplierSiret) ?? p.supplierSiret,
          badge: { label: s.label, variant: s.variant },
        };
      }),
    [campaign.participations, nameBySiret],
  );

  // §2.7.3 / A-16 — export CSV des destinataires (γ synchrone, back existant). LECTURE
  // SEULE : télécharge la cohorte courante (SIRET, état, invited_at) pour un broadcast manuel.
  const onExportRecipients = React.useCallback((): void => {
    exportsClient.downloadCampaignRecipients(campaign.campaignId);
  }, [exportsClient, campaign.campaignId]);

  const count = campaign.participations.length;

  return (
    <Panel
      headingId="detail-suppliers-heading"
      title={
        <>
          Fournisseurs <TitleMono>{plural(count, 'invité')}</TitleMono>
        </>
      }
      aside={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onExportRecipients}
          aria-label="Télécharger l'export CSV des destinataires de la campagne"
        >
          Exporter (CSV)
        </Button>
      }
    >
      <EntityScopePicker
        variant="panel"
        entityNoun="fournisseur"
        pickerLabel="Fournisseurs à ajouter"
        noun={{ singular: 'fournisseur', plural: 'fournisseurs', pastParticiple: 'ajouté' }}
        idLabel="SIRET"
        formatId={formatSiret}
        searchPlaceholder="Rechercher un fournisseur…"
        candidates={candidates}
        onSearchChange={setQuery}
        searchNote={
          truncated ? (
            <p className="text-xs text-muted-foreground">
              {`${String(page.items.length)} fournisseurs proposés sur ${String(page.total)} : précisez la recherche pour atteindre les autres.`}
            </p>
          ) : null
        }
        // L'API n'a PAS d'ajout en lot pour les participations : le picker enchaîne ces appels
        // unitaires séquentiellement et rend compte entrée par entrée (lib/sequential-add.ts).
        onAdd={(supplierSiret) =>
          add.mutateAsync({ campaignId: campaign.campaignId, body: { supplierSiret } })
        }
        members={members}
        onRemove={(entryId) => {
          remove.mutate({ campaignId: campaign.campaignId, entryId });
        }}
        removePending={remove.isPending}
        emptyMembersText="Aucun fournisseur participant."
      />
    </Panel>
  );
}
